import { useEffect, useState } from 'react';
import { Button } from '@fluent/ui';
import './SwUpdateToast.css';

/**
 * SwUpdateToast — listens for `fluent:sw-update` and shows a Georgian
 * "new version available" banner with an action button. Tapping the button
 * triggers the new SW's `skipWaiting` and reloads the page.
 *
 * Decoupled from `<ToastProvider>` because we don't have a global one
 * mounted yet (deferred until other features need it).
 */
export function SwUpdateToast() {
  const [pending, setPending] = useState<{ activate: () => void } | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    type UpdateEvent = CustomEvent<{ activate: () => void }>;
    const onUpdate = (e: Event) => {
      const detail = (e as UpdateEvent).detail;
      if (detail?.activate) setPending({ activate: detail.activate });
    };
    window.addEventListener('fluent:sw-update', onUpdate as EventListener);
    return () => window.removeEventListener('fluent:sw-update', onUpdate as EventListener);
  }, []);

  if (!pending) return null;

  const handleUpdate = () => {
    setUpdating(true);
    // Tell the new SW to skipWaiting + take control. On the happy path the
    // SW's `controlling` event fires in swRegistration.ts and reloads the
    // page. On iOS Safari (especially in standalone PWA mode) `controlling`
    // doesn't always fire after skipWaiting — Apple bug. So we force a
    // reload after 1.5s no matter what; by then the new SW has had time to
    // activate and the reload pulls the fresh shell either way.
    pending.activate();
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  return (
    <div
      className="fluent-sw-toast"
      role="status"
      aria-live="polite"
      data-testid="sw-update-toast"
    >
      <div className="fluent-sw-toast__body">
        <p className="fluent-sw-toast__title">ახალი ვერსია მზადაა</p>
        <p className="fluent-sw-toast__message">
          აპი განახლდა — გადატვირთე, რომ ახალი ფუნქციები გამოჩნდეს.
        </p>
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={handleUpdate}
        loading={updating}
        data-testid="sw-update-cta"
      >
        განახლება
      </Button>
    </div>
  );
}
